import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth token management
export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('authToken', token);
  } else {
    delete api.defaults.headers.common['Authorization'];
    localStorage.removeItem('authToken');
  }
};

// Initialize token from localStorage
const storedToken = localStorage.getItem('authToken');
if (storedToken) {
  setAuthToken(storedToken);
}

// ==================== PUBLIC APIs ====================

export const publicAPI = {
  getHero: () => api.get('/api/public/hero'),
  getCourses: () => api.get('/api/public/courses'),
  getCourse: (id) => api.get(`/api/public/courses/${id}`),
  getTeachers: () => api.get('/api/public/teachers'),
  getTeacher: (id) => api.get(`/api/public/teachers/${id}`),
  getTestimonials: () => api.get('/api/public/testimonials'),
  getFaqs: () => api.get('/api/public/faqs'),
  getCategories: () => api.get('/api/public/categories'),
  getValues: () => api.get('/api/public/values'),
  getCourseTypes: () => api.get('/api/public/course-types'),
  getMarqueeTeachers: () => api.get('/api/public/marquee-teachers'),
};

// ==================== AUTH APIs ====================

export const authAPI = {
  register: (data) => api.post('/api/auth/register', data),
  login: (data) => api.post('/api/auth/login', data),
  getMe: () => api.get('/api/auth/me'),
  updateMe: (data) => api.put('/api/auth/me', data),
};

// ==================== STUDENT APIs ====================

export const studentAPI = {
  getDashboard: () => api.get('/api/student/dashboard'),
  getCourses: () => api.get('/api/student/courses'),
  getCourse: (id) => api.get(`/api/student/courses/${id}`),
  getLiveSessions: () => api.get('/api/student/sessions/live'),
  getRecordings: (courseId = null) => 
    api.get('/api/student/recordings', { params: courseId ? { course_id: courseId } : {} }),
  getMaterials: (courseId = null) => 
    api.get('/api/student/materials', { params: courseId ? { course_id: courseId } : {} }),
};

// ==================== TEACHER APIs ====================

export const teacherAPI = {
  getDashboard: () => api.get('/api/teacher/dashboard'),
  getCourses: () => api.get('/api/teacher/courses'),
  getCourse: (id) => api.get(`/api/teacher/courses/${id}`),
  createCourse: (data) => api.post('/api/teacher/courses', data),
  deleteCourse: (id) => api.delete(`/api/teacher/courses/${id}`),
  getSessions: (params = {}) => api.get('/api/teacher/sessions', { params }),
  createSession: (data) => api.post('/api/teacher/sessions', data),
  updateSession: (id, data) => api.put(`/api/teacher/sessions/${id}`, data),
  deleteSession: (id) => api.delete(`/api/teacher/sessions/${id}`),
  setZoomLink: (sessionId, zoomLink, activate = true) => 
    api.put(`/api/teacher/sessions/${sessionId}/zoom`, null, { params: { zoom_link: zoomLink, activate } }),
  uploadRecordingFile: (sessionId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/teacher/sessions/${sessionId}/upload-recording`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadRecording: (sessionId, recordingUrl) => 
    api.put(`/api/teacher/sessions/${sessionId}/recording`, null, { params: { recording_url: recordingUrl } }),
  getMaterials: (courseId = null) => 
    api.get('/api/teacher/materials', { params: courseId ? { course_id: courseId } : {} }),
  uploadMaterialFile: (file, courseId, title, titleAr = null, sessionId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('course_id', courseId);
    formData.append('title', title);
    if (titleAr) formData.append('title_ar', titleAr);
    if (sessionId) formData.append('session_id', sessionId);
    return api.post('/api/teacher/upload/material', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadMaterial: (data) => api.post('/api/teacher/materials', data),
  deleteMaterial: (id) => api.delete(`/api/teacher/materials/${id}`),
  getQuizzes: (courseId = null) => 
    api.get('/api/teacher/quizzes', { params: courseId ? { course_id: courseId } : {} }),
  createQuiz: (data) => api.post('/api/teacher/quizzes', data),
  deleteQuiz: (id) => api.delete(`/api/teacher/quizzes/${id}`),
  getSchedule: () => api.get('/api/teacher/schedule'),
  updateProfile: (data) => api.put('/api/teacher/profile', null, { params: data }),
};

// ==================== VIDEO APIs ====================

export const videoAPI = {
  startSession: (sessionId) => api.post('/api/video/start-session', { session_id: sessionId }),
  joinSession: (sessionId) => api.post('/api/video/join-session', { session_id: sessionId }),
  endSession: (videoSessionId) => api.post(`/api/video/end-session/${videoSessionId}`),
  getSessionStatus: (sessionId) => api.get(`/api/video/session-status/${sessionId}`),
  getActiveSessions: () => api.get('/api/video/active-sessions'),
  uploadRecording: (sessionId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/video/upload-recording/${sessionId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// ==================== MODERATOR APIs ====================

export const moderatorAPI = {
  getDashboard: () => api.get('/api/moderator/dashboard'),
  getStudents: (params = {}) => api.get('/api/moderator/students', { params }),
  getStudent: (id) => api.get(`/api/moderator/students/${id}`),
  getCourses: () => api.get('/api/moderator/courses'),
  enrollStudent: (data) => api.post('/api/moderator/enroll', data),
  extendEnrollment: (enrollmentId, months) => 
    api.put(`/api/moderator/enrollments/${enrollmentId}/extend`, null, { params: { additional_months: months } }),
  cancelEnrollment: (enrollmentId) => api.delete(`/api/moderator/enrollments/${enrollmentId}`),
  // Teacher management
  createTeacher: (data) => api.post('/api/moderator/teachers', data),
  getTeachers: (status = null) => api.get('/api/moderator/teachers', { params: status ? { status } : {} }),
  // User management (teachers and students)
  getManageableUsers: (role = null, page = 1) => 
    api.get('/api/moderator/users', { params: { role, page, limit: 50 } }),
  getUserById: (userId) => api.get(`/api/moderator/users/${userId}`),
  updateUserCredentials: (userId, data) => api.put(`/api/moderator/users/${userId}/credentials`, data),
};

// ==================== ADMIN APIs ====================

export const adminAPI = {
  createUser: (data) => api.post('/api/auth/admin/create-user', data),
  getUsers: (role = null) => api.get('/api/auth/admin/users', { params: role ? { role } : {} }),
  updateUserRole: (userId, newRole) => api.put(`/api/auth/admin/users/${userId}/role`, null, { params: { new_role: newRole } }),
  deactivateUser: (userId) => api.delete(`/api/auth/admin/users/${userId}`),
  // Admin dashboard & teacher approval
  getDashboard: () => api.get('/api/admin/dashboard'),
  getPendingTeachers: (params = {}) => api.get('/api/admin/teachers/pending', { params }),
  getAllTeachers: (params = {}) => api.get('/api/admin/teachers/all', { params }),
  approveTeacher: (teacherId) => api.put(`/api/admin/teachers/${teacherId}/approve`),
  rejectTeacher: (teacherId, reason = null) => 
    api.put(`/api/admin/teachers/${teacherId}/reject`, null, { params: reason ? { reason } : {} }),
  revokeTeacher: (teacherId) => api.put(`/api/admin/teachers/${teacherId}/revoke`),
  // Teacher profile management
  uploadTeacherImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/admin/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  createTeacherProfile: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
        formData.append(key, data[key]);
      }
    });
    return api.post('/api/admin/teachers/create', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getTeacherDetails: (teacherId) => api.get(`/api/admin/teachers/${teacherId}`),
  deleteTeacherProfile: (teacherId) => api.delete(`/api/admin/teachers/${teacherId}`),
  updateTeacherProfile: (teacherId, data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    return api.put(`/api/admin/teachers/${teacherId}/profile`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // User management (all except admins)
  getAllUsers: (role = null, page = 1) => 
    api.get('/api/admin/users', { params: { role, page, limit: 50 } }),
  getAdminUserById: (userId) => api.get(`/api/admin/users/${userId}`),
  updateAdminUserCredentials: (userId, data) => api.put(`/api/admin/users/${userId}/credentials`, data),
  deleteUser: (userId) => api.delete(`/api/admin/users/${userId}`),
  // Student CSV export/import
  exportStudentsCSV: () => api.get('/api/admin/students/export', { responseType: 'blob' }),
  importStudentsCSV: (file, sendWelcomeEmails = false) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('send_emails', sendWelcomeEmails);
    return api.post('/api/admin/students/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getImportTemplate: () => api.get('/api/admin/students/export/template', { responseType: 'blob' }),
  // Marquee Teachers management
  getMarqueeTeachers: () => api.get('/api/admin/marquee-teachers'),
  createMarqueeTeacher: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    return api.post('/api/admin/marquee-teachers', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateMarqueeTeacher: (teacherId, data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    return api.put(`/api/admin/marquee-teachers/${teacherId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteMarqueeTeacher: (teacherId) => api.delete(`/api/admin/marquee-teachers/${teacherId}`),
};

// ==================== BOOKING APIs ====================

export const bookingAPI = {
  // Public (no auth)
  getTeacherAvailability: (teacherId) => api.get(`/api/booking/teachers/${teacherId}/availability`),
  getBookedSlots: (teacherId, startDate, endDate) => 
    api.get(`/api/booking/teachers/${teacherId}/booked-slots`, { params: { start_date: startDate, end_date: endDate } }),
  bookSession: (data) => api.post('/api/booking/book', data),
  // Teacher (requires auth)
  setAvailability: (data) => api.put('/api/booking/teacher/availability', data),
  getMyBookings: (status = null) => 
    api.get('/api/booking/teacher/bookings', { params: status ? { status } : {} }),
  confirmBooking: (bookingId, zoomLink = null) => 
    api.put(`/api/booking/teacher/bookings/${bookingId}/confirm`, null, { params: zoomLink ? { zoom_link: zoomLink } : {} }),
  cancelBooking: (bookingId, reason = null) => 
    api.put(`/api/booking/teacher/bookings/${bookingId}/cancel`, null, { params: reason ? { reason } : {} }),
};

// ==================== TEACHER PROFILE APIs ====================

export const teacherProfileAPI = {
  updateIntroVideo: (url) => api.put('/api/teacher/intro-video', null, { params: { intro_video_url: url } }),
  getIntroVideo: () => api.get('/api/teacher/intro-video'),
  deleteIntroVideo: () => api.delete('/api/teacher/intro-video'),
};

// ==================== GOOGLE AUTH APIs ====================

export const googleAuthAPI = {
  processSession: (sessionId) => api.post('/api/auth/google/session', { session_id: sessionId }),
  getMe: () => api.get('/api/auth/google/me'),
  logout: () => api.post('/api/auth/google/logout'),
};

export default api;
